import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { TIME_OF_DAY_CLOCK } from '@goaltree/backend/convex/lib/popularHabits'
import { Button } from '@goaltree/ui/components/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@goaltree/ui/components/dialog'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import type { OptimisticLocalStore } from 'convex/browser'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { Lightbulb, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { TimeField } from '@/components/time-field'
import { formatTimeLabel } from '@/lib/time'
import { handleUsageLimit } from '@/lib/usage-limit'

type Suggestion = NonNullable<FunctionReturnType<typeof api.habitSuggestions.reviewHabitSuggestion>>

function hidePending(store: OptimisticLocalStore, id: Id<'habitSuggestions'>) {
  for (const { args, value } of store.getAllQueries(
    api.habitSuggestions.myPendingHabitSuggestion,
  )) {
    if (value?._id === id) store.setQuery(api.habitSuggestions.myPendingHabitSuggestion, args, null)
  }
}

function useSuggestionActions() {
  const { t } = useTranslation()
  const accept = useMutation(api.habitSuggestions.acceptHabitSuggestion).withOptimisticUpdate(
    (store, { id }) => hidePending(store, id),
  )
  const dismiss = useMutation(api.habitSuggestions.dismissHabitSuggestion).withOptimisticUpdate(
    (store, { id }) => hidePending(store, id),
  )
  const respond = useMutation(
    api.habitSuggestions.respondToReviewHabitSuggestion,
  ).withOptimisticUpdate((store, { suggestion }) => {
    const cur = store.getQuery(api.habitSuggestions.reviewHabitSuggestion, {})
    if (cur?.title === suggestion.title)
      store.setQuery(api.habitSuggestions.reviewHabitSuggestion, {}, null)
  })
  return {
    async add(s: Suggestion, time?: string) {
      const opts = { time, recurrenceTzOffset: new Date().getTimezoneOffset() }
      try {
        if (s._id) await accept({ id: s._id, ...opts })
        else {
          const { title, period, timeOfDay, category } = s
          await respond({
            suggestion: { title, period, timeOfDay, category },
            accept: true,
            ...opts,
          })
        }
        toast.success(t('habitSuggestion.added'))
      } catch (err) {
        if (!handleUsageLimit(err)) toast.error(t('habitSuggestion.error'))
      }
    },
    dismiss(s: Suggestion) {
      const { title, period, timeOfDay, category } = s
      void (
        s._id
          ? dismiss({ id: s._id })
          : respond({ suggestion: { title, period, timeOfDay, category }, accept: false })
      ).catch(() => {})
    },
  }
}

function lead(s: Suggestion, t: ReturnType<typeof useTranslation>['t']) {
  if (s.trigger === 'milestone' && s.subjectTitle)
    return t('habitSuggestion.milestone', { milestone: s.subjectTitle })
  if (s.trigger === 'settled' && s.subjectTitle)
    return t('habitSuggestion.settled', { habit: s.subjectTitle })
  return t(s.trigger === 'review' ? 'habitSuggestion.review' : 'habitSuggestion.generic')
}

function Meta({ s, time }: { s: Suggestion; time: string }) {
  const { t } = useTranslation()
  return (
    <p className="text-xs text-muted-foreground">
      {t(`habitSuggestion.period.${s.period}`)} · {formatTimeLabel(time)}
    </p>
  )
}

function InlineCard({
  s,
  onAdd,
  onDismiss,
  className,
}: {
  s: Suggestion
  onAdd: () => void
  onDismiss: () => void
  className?: string
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3',
        className,
      )}
    >
      <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary-text/70" strokeWidth={1.5} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="text-xs text-muted-foreground">{lead(s, t)}</p>
        <p className="text-sm font-semibold text-foreground">{s.title}</p>
        <Meta s={s} time={TIME_OF_DAY_CLOCK[s.timeOfDay]} />
      </div>
      <Button size="sm" onClick={onAdd} className="shrink-0 self-center">
        {t('habitSuggestion.add')}
      </Button>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('habitSuggestion.dismiss')}
        className="mt-0.5 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded text-muted-foreground/40 transition-colors hover:text-muted-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}

// Today: a pending suggestion as a dismissible card, and — when opened from the
// push (/today?habitSuggestion=<id>) — as an "Add habit" dialog.
export function HabitSuggestionCard({ className }: { className?: string }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const linkedId = useRouterState({
    select: (r) => (r.location.search as { habitSuggestion?: string }).habitSuggestion,
  })
  const s = useQuery(
    api.habitSuggestions.myPendingHabitSuggestion,
    linkedId ? { id: linkedId as Id<'habitSuggestions'> } : {},
  )
  const { add, dismiss } = useSuggestionActions()
  const [time, setTime] = useState<string | null>(null)
  if (!s) return null

  const open = !!linkedId && s._id === linkedId
  const close = () => void navigate({ to: '/today', replace: true })
  const clock = time ?? TIME_OF_DAY_CLOCK[s.timeOfDay]

  return (
    <>
      {!open && (
        <InlineCard
          s={s}
          onAdd={() => void add(s)}
          onDismiss={() => dismiss(s)}
          className={className}
        />
      )}
      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Lightbulb className="h-4 w-4 text-primary-text/70" strokeWidth={1.5} />
              {t('habitSuggestion.addHabit')}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{lead(s, t)}</p>
          <div className="space-y-2 rounded-xl border border-border bg-card px-4 py-3">
            <p className="text-sm font-semibold text-foreground">{s.title}</p>
            <div className="flex items-center justify-between gap-3">
              <Meta s={s} time={clock} />
              <TimeField value={clock} onChange={setTime} ariaLabel={t('habitSuggestion.time')} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                dismiss(s)
                close()
              }}
            >
              {t('habitSuggestion.notNow')}
            </Button>
            <Button
              onClick={() => {
                void add(s, clock)
                close()
              }}
            >
              {t('habitSuggestion.addHabit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Weekly review Focus step: one suggested habit (in-app only).
export function ReviewHabitSuggestion({ className }: { className?: string }) {
  const s = useQuery(api.habitSuggestions.reviewHabitSuggestion)
  const { add, dismiss } = useSuggestionActions()
  const [hidden, setHidden] = useState(false)
  if (!s || hidden) return null
  return (
    <InlineCard
      s={s}
      onAdd={() => {
        setHidden(true)
        void add(s)
      }}
      onDismiss={() => {
        setHidden(true)
        dismiss(s)
      }}
      className={className}
    />
  )
}
